import { ConnectError } from '@bufbuild/connect';
import { yupResolver } from '@hookform/resolvers/yup';
import cn from 'classnames';
import { FC, useCallback, useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAppDispatch, useAppSelector, useMedia } from '@/shared/hooks';
import { formatDateToYYYYMMDD } from '@/shared/lib';
import { DatePicker, errorToastCaller, Input, Modal } from '@/shared/ui';

import { albumModel } from '@/entities/album';
import { ModalID, modalModel } from '@/entities/modal';
import { userModel } from '@/entities/user';

import {
  EditAlbumFormPayload,
  editAlbumFormSchema,
} from '../config/formSchema';

import styles from './EditAlbumModal.module.scss';

type EditAlbumModalProps = {
  className?: string;
  testID?: string;
};

export const EditAlbumModal: FC<EditAlbumModalProps> = (props) => {
  const { className, testID } = props;
  const { formatMessage } = useIntl();
  const { isMobile } = useMedia();

  const dispatch = useAppDispatch();

  const locale = useAppSelector(userModel.selectors.selectUserLocale);

  const { visible, data } = useAppSelector(
    modalModel.selectors.selectEditAlbumModal,
  );

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
    reset,
    setValue,
  } = useForm<EditAlbumFormPayload>({
    resolver: yupResolver(editAlbumFormSchema),
  });

  const handleClose = useCallback(() => {
    dispatch(modalModel.actions.closeModal(ModalID.EditAlbumModal));
    reset();
  }, [reset]);

  const onSubmit: SubmitHandler<EditAlbumFormPayload> = useCallback(
    async (formData) => {
      try {
        const isSameDay = (date1: Date, date2: Date) => {
          return (
            date1.getFullYear() === date2.getFullYear() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getDate() === date2.getDate()
          );
        };

        const createdAt = data.time;
        const selectedDate = formData.albumTime;
        const dateToSend =
          createdAt && isSameDay(selectedDate, createdAt)
            ? createdAt
            : selectedDate;

        const { DentalPhotoAlbum } = await dispatch(
          albumModel.thunks.editAlbum({
            AlbumID: data.albumID,
            Name: formData.albumName,
            Time: {
              // @ts-expect-error WARN: if you send Timestamp back fail
              seconds: Math.floor(dateToSend.getTime() / 1000),
            },
          }),
        ).unwrap();

        if (DentalPhotoAlbum) {
          dispatch(albumModel.actions.setNewestOne(DentalPhotoAlbum));
        }

        handleClose();
      } catch (error) {
        errorToastCaller(error as ConnectError);
      }
    },
    [data.albumID, dispatch, handleClose],
  );

  useEffect(() => {
    if (data.albumID) {
      setValue('albumName', data.albumName);
      setValue('albumTime', data?.time ? new Date(data.time) : new Date());
    }

    return () => reset();
  }, [data.albumID, data.albumName, data.time, reset, setValue]);

  return (
    <Modal
      data-component="edit-album-modal"
      isOpen={visible}
      data-testid={testID}
      className={cn(styles.container, className)}
      title={formatMessage({
        id: 'editAlbumModal.title',
        defaultMessage: 'Edit album',
      })}
      onCancel={handleClose}
      okButtonProps={{
        type: 'submit',
        form: 'editAlbumModal',
        loading: isSubmitting,
      }}
      okButtonText={formatMessage({
        id: 'global.save',
        defaultMessage: 'Save',
      })}
      onOk={handleSubmit(onSubmit)}
      shouldRenderCancelButton={!isMobile}
      borderless={isMobile}
    >
      <form id="editAlbumModal" onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.row}>
          <Controller
            control={control}
            name="albumName"
            render={({
              field: { ref, value, name, onBlur, onChange },
              fieldState: { error },
            }) => (
              <Input
                ref={ref}
                value={value}
                name={name}
                onBlur={onBlur}
                onChange={onChange}
                type="text"
                label={formatMessage({
                  id: 'global.albumName',
                  defaultMessage: 'Album name',
                })}
                error={error?.message}
              />
            )}
          />

          <Controller
            control={control}
            name="albumTime"
            render={({
              field: { name, value, onChange },
              fieldState: { error },
            }) =>
              isMobile ? (
                <Input
                  name={name}
                  type="date"
                  inputMode="numeric"
                  value={formatDateToYYYYMMDD(value)}
                  onChange={(event) => {
                    onChange(new Date(event.target.value));
                  }}
                  label={
                    <FormattedMessage
                      id="album.edit.label.date"
                      defaultMessage="Album date"
                    />
                  }
                  error={error?.message}
                  required
                />
              ) : (
                <DatePicker
                  locale={locale}
                  value={value}
                  maxDate={new Date()}
                  onChange={onChange}
                  placeholder={formatMessage({
                    id: 'album.edit.placeholder.selectDate',
                    defaultMessage: 'Select date',
                  })}
                  label={
                    <FormattedMessage
                      id="album.edit.label.date"
                      defaultMessage="Album date"
                    />
                  }
                  error={error?.message}
                />
              )
            }
          />
        </div>
      </form>
    </Modal>
  );
};
